import { useState } from "react"
import { FileText } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { StatusBadge } from "@/components/ui/status-badge"
import { formatDateTime, formatMoney } from "@/lib/formatters"
import { patchInline, postInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"
import {
  INVOICE_PATHS,
  TAX_PROFILE_PATH,
  invoiceAmount,
  invoiceTone,
  tInvoice,
  type BillInvoice,
  type CfdiSettings,
} from "@/features/staff/invoices/invoices"

/**
 * The CFDI section of the bill drawer: what document this bill has, or the one
 * button that opens one.
 *
 * ## Two "you cannot invoice" states, two different sentences
 *
 * A restaurant whose platform admin has not granted `cfdi_invoicing` and a
 * restaurant that has not entered its RFC are different problems with different
 * people to talk to, and the manager has to be sent to the right one. The first
 * never reaches this component at all — the server sends `cfdi: null` and the
 * drawer renders nothing, because a section about a feature nobody has is noise.
 * The second renders a sentence and a link to the screen that fixes it.
 *
 * ## Issuing is a ceremony, and it is one the diner is standing there for
 *
 * The receiver is four fields copied off whatever the diner handed over, and
 * every one of them is on the document permanently. So it is a dialog with a
 * confirm, not an inline commit — and the fields are typed rather than picked
 * from a saved profile, because Menny deliberately stores no reusable diner RFC
 * (a document must not change when somebody later edits a profile).
 *
 * ## Cancelling asks for a motive from the SAT's own catalog
 *
 * Free text is refused for the reason `Location::RefundPolicy` gives about its
 * own list, with more force: this motive travels to the SAT and becomes part of
 * the permanent record of the sale. Motive `01` carries a rule with it — it
 * means "this other folio replaces it" — so the replacing UUID is required
 * alongside it, here and in `Location::Invoice`'s own validation.
 */

type InvoicePanelProps = {
  billId: number
  billPaid: boolean
  invoice: BillInvoice | null
  cfdi: CfdiSettings
  /** The restaurant's IANA zone. A stamp is timestamped where the restaurant is. */
  timeZone?: string
}

export function InvoicePanel({ billId, billPaid, invoice, cfdi, timeZone }: InvoicePanelProps) {
  return (
    <section className="space-y-3" data-testid={`bill-invoice-${billId}`}>
      <p className="text-sm font-medium">{tInvoice("title")}</p>

      {invoice ? (
        <IssuedInvoice invoice={invoice} cfdi={cfdi} timeZone={timeZone} />
      ) : (
        <NoInvoiceYet billId={billId} billPaid={billPaid} cfdi={cfdi} />
      )}
    </section>
  )
}

/**
 * What exists, and the one action left on it.
 *
 * A `pending` invoice says so instead of showing an empty folio field: the row
 * exists before the stamp does, and "sin folio" with no explanation reads as a
 * bug rather than as a queue.
 */
function IssuedInvoice({
  invoice,
  cfdi,
  timeZone,
}: {
  invoice: BillInvoice
  cfdi: CfdiSettings
  timeZone?: string
}) {
  return (
    <div className="space-y-3 rounded-lg border bg-card p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <StatusBadge tone={invoiceTone(invoice.status)} label={invoice.statusLabel} />
        <span className="text-sm font-semibold tabular-nums text-foreground" data-testid="invoice-total">
          {formatMoney(invoiceAmount(invoice.totalCents), { currency: invoice.currency })}
        </span>
      </div>

      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-xs">
        <dt className="text-muted-foreground">{tInvoice("folio_label")}</dt>
        <dd className="min-w-0 break-all font-mono tabular-nums" data-testid="invoice-folio">
          {invoice.uuid ?? tInvoice("folio_pending")}
        </dd>

        <dt className="text-muted-foreground">{tInvoice("receiver_label")}</dt>
        <dd className="min-w-0 truncate" data-testid="invoice-receiver">
          {invoice.receiverRfc} · {invoice.receiverLegalName}
        </dd>

        {invoice.stampedAt && (
          <>
            <dt className="text-muted-foreground">{tInvoice("issued_at_label")}</dt>
            <dd className="tabular-nums" data-testid="invoice-stamped-at">
              {formatDateTime(invoice.stampedAt, { timeZone })}
            </dd>
          </>
        )}

        {invoice.cancellationReasonLabel && (
          <>
            <dt className="text-muted-foreground">{tInvoice("cancel_reason_label")}</dt>
            <dd className="min-w-0" data-testid="invoice-cancellation-reason">
              {invoice.cancellationReasonLabel}
            </dd>
          </>
        )}
      </dl>

      {invoice.status === "pending" && (
        <p className="text-xs text-muted-foreground" data-testid="invoice-pending-notice">
          {tInvoice("status_pending")}
        </p>
      )}

      {invoice.status === "cancellation_pending" && (
        <p
          className="rounded-md border border-warning/40 bg-warning/10 p-2.5 text-xs"
          data-testid="invoice-cancellation-pending-notice"
        >
          {tInvoice("cancellation_pending_notice")}
        </p>
      )}

      {/* `lastError` and not the SAT code: a refusal that never reached a PAC has
          no code at all, and this is the field written on both paths. */}
      {invoice.lastError && (
        <div className="space-y-1" data-testid="invoice-error">
          <p className="text-xs font-medium text-muted-foreground">{tInvoice("error_label")}</p>
          <p className="text-xs text-destructive">{invoice.lastError}</p>
          {invoice.exhausted && (
            <p className="text-xs text-muted-foreground" data-testid="invoice-exhausted">
              {tInvoice("exhausted_notice")}
            </p>
          )}
        </div>
      )}

      {invoice.cancellable && cfdi.canIssue && <CancelInvoiceAction invoice={invoice} cfdi={cfdi} />}
    </div>
  )
}

/**
 * Why there is no invoice, or the button that opens one.
 *
 * Each refusal names what would fix it. A blank space where a button belongs is
 * how a manager concludes the feature is broken.
 */
function NoInvoiceYet({
  billId,
  billPaid,
  cfdi,
}: {
  billId: number
  billPaid: boolean
  cfdi: CfdiSettings
}) {
  if (!cfdi.configured) {
    return (
      <div
        className="space-y-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm"
        data-testid="invoice-not-configured"
      >
        <p>{tInvoice("not_configured")}</p>
        <Button asChild variant="outline" size="sm">
          {/* On the anchor, not the Button: `asChild` renders this element, so
              this is the control a test can actually click. */}
          <a href={TAX_PROFILE_PATH} data-testid="invoice-not-configured-link">
            {tInvoice("not_configured_link")}
          </a>
        </Button>
      </div>
    )
  }

  if (!billPaid) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="invoice-bill-not-paid">
        {tInvoice("bill_not_paid")}
      </p>
    )
  }

  if (!cfdi.canIssue) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="invoice-no-permission">
        {tInvoice("no_permission")}
      </p>
    )
  }

  return <IssueInvoiceAction billId={billId} cfdi={cfdi} />
}

function IssueInvoiceAction({ billId, cfdi }: { billId: number; cfdi: CfdiSettings }) {
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [receiverRfc, setReceiverRfc] = useState("")
  const [receiverLegalName, setReceiverLegalName] = useState("")
  const [receiverPostalCode, setReceiverPostalCode] = useState("")
  const [receiverTaxRegimeCode, setReceiverTaxRegimeCode] = useState("")
  const [usageCode, setUsageCode] = useState(cfdi.defaultUsageCode)

  const complete =
    receiverRfc.trim() !== "" &&
    receiverLegalName.trim() !== "" &&
    receiverPostalCode.trim() !== "" &&
    receiverTaxRegimeCode !== "" &&
    usageCode !== ""

  const handleOpenChange = (next: boolean) => {
    // Escape and the overlay both route through here. A dialog that vanishes
    // mid-request leaves the manager unsure whether a stamp was spent.
    if (submitting) return

    setOpen(next)
  }

  const handleConfirm = () => {
    if (!complete || submitting) return

    setSubmitting(true)
    postInline(
      INVOICE_PATHS.create(billId),
      {
        receiver_rfc: receiverRfc.trim(),
        receiver_legal_name: receiverLegalName.trim(),
        receiver_postal_code: receiverPostalCode.trim(),
        receiver_tax_regime_code: receiverTaxRegimeCode,
        usage_code: usageCode,
      },
      // `preserveState` keeps the dialog mounted through the response, so a
      // refusal the server reports as a flash alert — a malformed RFC most of
      // all — lands while the manager is still looking at what they typed.
      { preserveState: true }
    )
      .then((result) => {
        setOpen(false)
        toastInlineUpdateSuccess(result)
      })
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setSubmitting(false))
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="gap-1.5"
        onClick={() => handleOpenChange(true)}
        data-testid={`invoice-open-${billId}`}
      >
        <FileText className="size-3.5" />
        {tInvoice("issue_open")}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>{tInvoice("issue_title")}</DialogTitle>
            <DialogDescription>{tInvoice("issue_description")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invoice-receiver-rfc">{tInvoice("rfc_label")}</Label>
              <Input
                id="invoice-receiver-rfc"
                value={receiverRfc}
                onChange={(event) => setReceiverRfc(event.target.value)}
                placeholder={tInvoice("rfc_placeholder")}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                data-testid="invoice-receiver-rfc"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice-receiver-legal-name">{tInvoice("legal_name_label")}</Label>
              <Input
                id="invoice-receiver-legal-name"
                value={receiverLegalName}
                onChange={(event) => setReceiverLegalName(event.target.value)}
                placeholder={tInvoice("legal_name_placeholder")}
                data-testid="invoice-receiver-legal-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice-receiver-postal-code">{tInvoice("postal_code_label")}</Label>
              <Input
                id="invoice-receiver-postal-code"
                inputMode="numeric"
                value={receiverPostalCode}
                onChange={(event) => setReceiverPostalCode(event.target.value)}
                placeholder={tInvoice("postal_code_placeholder")}
                className="tabular-nums"
                data-testid="invoice-receiver-postal-code"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice-receiver-tax-regime-code">{tInvoice("tax_regime_label")}</Label>
              <Select value={receiverTaxRegimeCode} onValueChange={setReceiverTaxRegimeCode}>
                <SelectTrigger
                  id="invoice-receiver-tax-regime-code"
                  className="w-full"
                  data-testid="invoice-receiver-tax-regime-code"
                >
                  <SelectValue placeholder={tInvoice("tax_regime_placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {cfdi.taxRegimeCodes.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      data-testid={`invoice-receiver-tax-regime-code-option-${option.value}`}
                    >
                      {option.value} · {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invoice-usage-code">{tInvoice("usage_label")}</Label>
              <Select value={usageCode} onValueChange={setUsageCode}>
                <SelectTrigger id="invoice-usage-code" className="w-full" data-testid="invoice-usage-code">
                  <SelectValue placeholder={tInvoice("usage_placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {cfdi.usageCodes.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      data-testid={`invoice-usage-code-option-${option.value}`}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={() => handleOpenChange(false)}
              data-testid="invoice-cancel"
            >
              {tInvoice("cancel")}
            </Button>
            <Button
              type="button"
              disabled={!complete || submitting}
              onClick={handleConfirm}
              data-testid="invoice-confirm"
            >
              {submitting ? tInvoice("submitting") : tInvoice("confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function CancelInvoiceAction({ invoice, cfdi }: { invoice: BillInvoice; cfdi: CfdiSettings }) {
  const [open, setOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [reason, setReason] = useState("")
  const [substituteUuid, setSubstituteUuid] = useState("")

  const needsSubstitute = reason === cfdi.substitutionReasonCode
  const complete = reason !== "" && (!needsSubstitute || substituteUuid.trim() !== "")

  const handleOpenChange = (next: boolean) => {
    if (submitting) return

    setOpen(next)
    if (!next) {
      setReason("")
      setSubstituteUuid("")
    }
  }

  const handleConfirm = () => {
    if (!complete || submitting) return

    setSubmitting(true)
    patchInline(
      INVOICE_PATHS.cancel(invoice.id),
      { cancellation_reason_code: reason, substitute_uuid: substituteUuid.trim() },
      { preserveState: true }
    )
      .then((result) => {
        setOpen(false)
        setReason("")
        setSubstituteUuid("")
        toastInlineUpdateSuccess(result)
      })
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setSubmitting(false))
  }

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={() => handleOpenChange(true)}
        data-testid={`invoice-cancel-open-${invoice.id}`}
      >
        {tInvoice("cancel_open")}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent data-testid="invoice-cancel-dialog">
          <DialogHeader>
            <DialogTitle>{tInvoice("cancel_title")}</DialogTitle>
            <DialogDescription>{tInvoice("cancel_description")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="invoice-cancellation-reason-code">{tInvoice("cancel_reason_label")}</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger
                  id="invoice-cancellation-reason-code"
                  className="w-full"
                  data-testid="invoice-cancellation-reason-code"
                >
                  <SelectValue placeholder={tInvoice("cancel_reason_placeholder")} />
                </SelectTrigger>
                <SelectContent>
                  {cfdi.cancellationReasons.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      data-testid={`invoice-cancellation-reason-code-option-${option.value}`}
                    >
                      {option.value} · {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Only for motive 01. The field appears with the rule rather than
                sitting there permanently asking for something that usually does
                not apply. */}
            {needsSubstitute && (
              <div className="space-y-2">
                <Label htmlFor="invoice-substitute-uuid">{tInvoice("substitute_label")}</Label>
                <Input
                  id="invoice-substitute-uuid"
                  value={substituteUuid}
                  onChange={(event) => setSubstituteUuid(event.target.value)}
                  placeholder={tInvoice("substitute_placeholder")}
                  autoComplete="off"
                  spellCheck={false}
                  aria-describedby="invoice-substitute-uuid-hint"
                  data-testid="invoice-substitute-uuid"
                />
                <p id="invoice-substitute-uuid-hint" className="text-xs text-muted-foreground">
                  {tInvoice("substitute_hint")}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={() => handleOpenChange(false)}
              data-testid="invoice-cancel-dismiss"
            >
              {tInvoice("cancel")}
            </Button>
            {/* `--destructive-foreground` is declared nowhere in the theme, so
                destructive surfaces carry literal white, as the kit does. */}
            <Button
              type="button"
              variant="destructive"
              className="text-white"
              disabled={!complete || submitting}
              onClick={handleConfirm}
              data-testid="invoice-cancel-confirm"
            >
              {submitting ? tInvoice("submitting") : tInvoice("cancel_confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
